import type { CSSProperties, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { TlaaqLogo } from '../brand/TlaaqLogo'
import { LocaleSwitcher } from '../locale/LocaleSwitcher'
import { Avatar } from '../ui/Avatar'
import { Button, Icon, I, Pill } from '../ui'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'
import type { AuthUser } from '../../auth/types'
import type { Locale } from '../../i18n'

export type AdminNavPage = 'admin-home' | 'admin-organizations' | 'admin-api-calls' | 'admin-scheduled-tasks' | 'admin-provider-proxy'

interface AdminShellProps {
  active: AdminNavPage
  title: string
  subtitle?: string
  viewer: AuthUser
  onNavigate: (page: AdminNavPage) => void
  onLogout: () => void
  onLocaleChange?: (locale: Locale) => void | Promise<void>
  children: ReactNode
}

interface AdminNavItem {
  id: AdminNavPage
  labelKey: string
  icon: ReactNode
}

const ADMIN_NAV: AdminNavItem[] = [
  { id: 'admin-home', labelKey: 'admin.nav.home', icon: I.shield },
  { id: 'admin-organizations', labelKey: 'admin.nav.organizations', icon: I.building },
  { id: 'admin-api-calls', labelKey: 'admin.nav.apiCalls', icon: I.trend },
  { id: 'admin-scheduled-tasks', labelKey: 'admin.nav.scheduledTasks', icon: I.clock },
  { id: 'admin-provider-proxy', labelKey: 'admin.nav.providerProxy', icon: I.zap },
]

export function AdminShell({ active, title, subtitle, viewer, onNavigate, onLogout, onLocaleChange, children }: AdminShellProps) {
  const isMobile = useIsMobileViewport()

  return (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', background: 'var(--bg)', height: '100%', minWidth: 0, overflow: 'hidden' }}>
      {!isMobile && <AdminSidebar active={active} onNavigate={onNavigate} viewer={viewer} />}
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <AdminTopBar title={title} subtitle={subtitle} viewer={viewer} onLogout={onLogout} onLocaleChange={onLocaleChange} isMobile={isMobile} />
        {isMobile && <AdminMobileNav active={active} onNavigate={onNavigate} />}
        <div style={{ flex: 1, padding: isMobile ? '18px 14px 96px' : '28px 32px', overflowY: 'auto', overflowX: 'hidden', minWidth: 0 }}>
          {children}
        </div>
      </main>
    </div>
  )
}

function AdminSidebar({ active, onNavigate, viewer }: { active: AdminNavPage; onNavigate: (page: AdminNavPage) => void; viewer: AuthUser }) {
  const { t } = useTranslation()

  return (
    <aside style={{
      width: 232,
      background: 'white',
      borderInlineEnd: '1px solid var(--border-subtle)',
      padding: '20px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      flexShrink: 0,
    }}>
      <div style={{ padding: '6px 8px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <TlaaqLogo height={24} />
        <Pill tone="coral" size="sm">{t('admin.shell.badge')}</Pill>
      </div>

      <SidebarGroup label={t('admin.nav.label')} />
      {ADMIN_NAV.map(item => (
        <AdminSidebarItem key={item.id} item={item} label={t(item.labelKey)} active={active} onClick={() => onNavigate(item.id)} />
      ))}

      <div style={{ flex: 1 }} />
      <AdminOrgChip viewer={viewer} />
    </aside>
  )
}

function SidebarGroup({ label }: { label: string }) {
  return (
    <div style={{
      fontSize: 10.5,
      fontWeight: 800,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: 'var(--fg-subtle)',
      padding: '14px 10px 6px',
    }}>
      {label}
    </div>
  )
}

function AdminSidebarItem({ item, label, active, onClick }: { item: AdminNavItem; label: string; active: AdminNavPage; onClick: () => void }) {
  const isActive = item.id === active

  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '9px 11px',
        borderRadius: 9,
        border: 'none',
        cursor: 'pointer',
        textAlign: 'start',
        background: isActive ? 'var(--brand-100)' : 'transparent',
        color: isActive ? 'var(--brand-700)' : 'var(--fg-muted)',
        fontWeight: isActive ? 700 : 500,
        fontSize: 13.5,
        fontFamily: 'inherit',
        width: '100%',
        position: 'relative',
        transition: 'all 150ms var(--ease-out)',
      }}
    >
      {isActive && (
        <div style={{
          position: 'absolute',
          insetInlineStart: 0,
          top: 8,
          bottom: 8,
          width: 3,
          borderRadius: 2,
          background: 'var(--brand-500)',
        }} />
      )}
      <Icon size={17}>{item.icon}</Icon>
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  )
}

function AdminMobileNav({ active, onNavigate }: { active: AdminNavPage; onNavigate: (page: AdminNavPage) => void }) {
  const { t } = useTranslation()

  return (
    <nav
      aria-label={t('admin.nav.label')}
      style={{
        display: 'flex',
        gap: 8,
        overflowX: 'auto',
        padding: '8px 14px',
        background: 'white',
        borderBottom: '1px solid var(--border-subtle)',
        flexShrink: 0,
      }}
    >
      {ADMIN_NAV.map(item => {
        const isActive = item.id === active
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onNavigate(item.id)}
            aria-current={isActive ? 'page' : undefined}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              paddingBlock: 8,
              paddingInline: 11,
              borderRadius: 999,
              border: isActive ? '1px solid var(--brand-300)' : '1px solid var(--border-subtle)',
              background: isActive ? 'var(--brand-100)' : 'var(--bg-card)',
              color: isActive ? 'var(--brand-700)' : 'var(--fg-muted)',
              fontSize: 12.5,
              fontWeight: isActive ? 800 : 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <Icon size={15}>{item.icon}</Icon>
            <span>{t(item.labelKey)}</span>
          </button>
        )
      })}
    </nav>
  )
}

function AdminOrgChip({ viewer }: { viewer: AuthUser }) {
  const mark = viewer.organizationName.trim().charAt(0).toUpperCase() || 'T'
  const emailDomain = viewer.email.split('@')[1] ?? viewer.email

  return (
    <div style={{
      padding: 12,
      borderRadius: 12,
      background: 'var(--ink-50)',
      border: '1px solid var(--border-subtle)',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      <div style={{
        width: 30,
        height: 30,
        borderRadius: 8,
        background: 'var(--brand-900)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 800,
        fontFamily: 'Nunito, sans-serif',
        flexShrink: 0,
      }}>
        {mark}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {viewer.organizationName}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--fg-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emailDomain}</div>
      </div>
    </div>
  )
}

function AdminTopBar({ title, subtitle, viewer, onLogout, onLocaleChange, isMobile }: {
  title: string
  subtitle?: string
  viewer: AuthUser
  onLogout: () => void
  onLocaleChange?: (locale: Locale) => void | Promise<void>
  isMobile: boolean
}) {
  const { t } = useTranslation()

  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      padding: isMobile ? '12px 14px' : '14px 32px',
      borderBottom: '1px solid var(--border-subtle)',
      background: 'rgba(255,255,255,0.85)',
      backdropFilter: 'blur(10px)',
      gap: isMobile ? 12 : 24,
      flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
        {isMobile && <TlaaqLogo height={24} />}
        <TopBarTitle title={title} subtitle={subtitle} badge={t('admin.shell.badge')} isMobile={isMobile} />
      </div>
      <div style={{ ...topBarActionsStyle, width: isMobile ? '100%' : undefined, overflowX: isMobile ? 'auto' : undefined, paddingBottom: isMobile ? 2 : undefined }}>
        <Button kind="secondary" size="sm" onClick={onLogout}>{t('nav.topbar.signOut')}</Button>
        <div style={{ flexShrink: 0 }}>
          <LocaleSwitcher onLocaleChange={onLocaleChange} />
        </div>
        <div style={{ flexShrink: 0 }}>
          <Avatar name={viewer.fullName} size={34} />
        </div>
      </div>
    </div>
  )
}

function TopBarTitle({ title, subtitle, badge, isMobile }: { title: string; subtitle?: string; badge: string; isMobile: boolean }) {
  return (
    <div style={{ minWidth: 0, width: isMobile ? '100%' : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: 0, lineHeight: 1.2, margin: 0, overflowWrap: 'normal', wordBreak: 'normal' }}>
          {title}
        </h1>
        <Pill tone="brand" dot>{badge}</Pill>
      </div>
      {subtitle && <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 2 }}>{subtitle}</div>}
    </div>
  )
}

const topBarActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'nowrap',
  justifyContent: 'flex-start',
  flexShrink: 0,
  maxWidth: '100%',
}
